/**
 * Lomakkeen sääntövalidoinnin puhtaat apurit (WP-F2, app-builder-forms-plan.md §4).
 * Ei DOMia — runtime.ts:n form-lohko lukee `<script class="sp-app-form-validators">`
 * -lohkon, kerää arvot ja kutsuu näitä.
 *
 * <p><b>Tämä ei ole lauseke-kieli.</b> Säännöt ovat DATAA (kenttä + sääntönimi +
 * parametri), eivät evaluoitavaa koodia, joten expression-kielioppiin ei kosketa
 * eikä `grammar-corpus.json`/`GrammarCorpusConformanceTest` muutu. Sama raja kuin
 * lifecycle-actioneilla: konfiguraatiota, ei koodia.
 *
 * <p><b>Suhde per-kenttä-`required`iin:</b> `data-app-input-required` on ensisijainen
 * ja ajetaan runtimessa ensin. Nämä säännöt täydentävät sitä; kenttä joka on jo
 * virheellinen ei saa toista virhettä (ensimmäinen osuma per kenttä voittaa).
 */
import { resolveMessage } from './messages';

/** Tuetut sääntönimet. Uusi sääntö = uusi haara {@link applyRule}en + testi. */
export type ValidationRuleName =
  | 'required'
  | 'regex'
  | 'minLength'
  | 'maxLength'
  | 'min'
  | 'max'
  | 'email';

export interface ValidationRule {
  /**
   * Kentän nimi = `data-app-input-name` (sama avain kuin values-slotissa), tai
   * repeaterin rivikenttä muodossa `<repeaterin name>[].<kenttä>` — ks. {@link parseRowField}.
   */
  field: string;
  rule: ValidationRuleName;
  /** Sääntöparametri: regexin lähde, pituus- tai lukuraja. */
  value?: unknown;
  /** Näytettävä virhe; tyhjä → {@link defaultMessage}. */
  message?: string;
}

/**
 * Sanaston AINOA lista. Editorin sääntövalitsin (ValidatorsEditor) lukee tämän,
 * joten editori ei voi tarjota sääntöä jota runtime ei tunne — sama
 * totuuslähde-periaate kuin komponenttikatalogilla.
 */
export const VALIDATION_RULE_NAMES: readonly ValidationRuleName[] = [
  'required', 'regex', 'minLength', 'maxLength', 'min', 'max', 'email',
];

/** Ottavatko sääntö parametrin (`value`). required/email eivät ota. */
export function ruleTakesValue(rule: ValidationRuleName): boolean {
  return rule !== 'required' && rule !== 'email';
}

const RULE_NAMES: ReadonlySet<string> = new Set<ValidationRuleName>(VALIDATION_RULE_NAMES);

/**
 * Sähköpostin muototarkistus. Tarkoituksella VÄLJÄ: tavoite on napata
 * kirjoitusvirhe ("matti.at.example.com"), ei toteuttaa RFC 5322:ta — liian
 * tiukka kuvio hylkää päteviä osoitteita, mikä on käyttäjälle pahempi vika kuin
 * läpi menevä epäosoite (jonka palvelin joka tapauksessa torjuu).
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** `signers[].email` → repeaterin `signers` jokaisen rivin kenttä `email`. */
const ROW_FIELD = /^([^[\].]+)\[\]\.([^[\].]+)$/;

/**
 * Repeaterin rivikentän sääntö (`signers[].email`): sääntö ajetaan jokaisella
 * rivillä, ja virhe avainnetaan rivin indeksoidulla polulla (`signers[1].email`)
 * — samalla avaimella jolla rivin oma `required` jo avainnetaan, joten virhe
 * maalautuu oikealle riville. Ennen tätä rivin kenttään ei voinut kohdistaa
 * sääntöä lainkaan: `signers[].name` luettiin litteänä kenttänimenä, jota
 * lomakkeessa ei ole, ja `required` lukitsi lomakkeen näkymättömällä virheellä.
 */
export function parseRowField(field: string): { list: string; field: string } | null {
  const match = ROW_FIELD.exec(field);
  return match ? { list: match[1], field: match[2] } : null;
}

/**
 * Parsii validators-lohkon. Sietää rikkinäistä syötettä palauttamalla tyhjän
 * listan: lomakkeen on toimittava (ilman lisäsääntöjä) vaikka konfiguraatio
 * olisi viallinen — rikkinäinen JSON ei saa lukita lomaketta.
 */
export function parseRules(raw: string | null | undefined): ValidationRule[] {
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const rules: ValidationRule[] = [];
  for (const entry of parsed) {
    if (!entry || typeof entry !== 'object') continue;
    const { field, rule, value, message } = entry as Record<string, unknown>;
    // Tuntematon sääntönimi tai nimetön kenttä ohitetaan yksitellen — yksi
    // kirjoitusvirhe ei saa mitätöidä muita sääntöjä.
    if (typeof field !== 'string' || !field) continue;
    if (typeof rule !== 'string' || !RULE_NAMES.has(rule)) continue;
    rules.push({
      field,
      rule: rule as ValidationRuleName,
      ...(value === undefined ? {} : { value }),
      ...(typeof message === 'string' && message ? { message } : {}),
    });
  }
  return rules;
}

/** Onko arvo "tyhjä" required-mielessä. Tyyppikohtainen: checkbox → false = tyhjä. */
export function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'boolean') return !value;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

/**
 * Oletusviesti kun sääntö ei anna omaansa. Teksti tulee viestikatalogista
 * (WP-VM1, {@link resolveMessage}), joten se noudattaa Appin `metadata.locale`a
 * ja `metadata.messages`-ohituksia. Säännön oma `message` voittaa yhä tämän —
 * ks. {@link applyRule}.
 */
export function defaultMessage(rule: ValidationRuleName, param: unknown): string {
  switch (rule) {
    case 'required': return resolveMessage('required');
    case 'regex': return resolveMessage('regex');
    case 'minLength': return resolveMessage('minLength', param);
    case 'maxLength': return resolveMessage('maxLength', param);
    case 'min': return resolveMessage('min', param);
    case 'max': return resolveMessage('max', param);
    case 'email': return resolveMessage('email');
    default: return resolveMessage('invalid');
  }
}

/**
 * Yhden säännön tulos: virheteksti tai null (menee läpi / ei sovellu).
 *
 * <p>TYHJÄ ARVO menee läpi kaikista säännöistä paitsi `required`ista. Muuten
 * pelkän `regex`-säännön saava valinnainen kenttä olisi käytännössä pakollinen —
 * "täytä oikein tai jätä täyttämättä" on se mitä valinnainen kenttä tarkoittaa.
 */
export function applyRule(rule: ValidationRule, value: unknown): string | null {
  const fail = () => rule.message || defaultMessage(rule.rule, rule.value);

  if (rule.rule === 'required') {
    return isEmptyValue(value) ? fail() : null;
  }
  if (isEmptyValue(value)) return null;

  switch (rule.rule) {
    case 'regex': {
      const source = typeof rule.value === 'string' ? rule.value : '';
      if (!source) return null;
      let re: RegExp;
      try {
        re = new RegExp(source);
      } catch (e) {
        // Rikkinäinen kuvio EI saa kaataa lomaketta: sääntö ohitetaan ja
        // virhe lokitetaan, jotta tekijä näkee sen konsolista.
        console.warn(`[app-runtime] invalid regex in validator for "${rule.field}": ${source}`, e);
        return null;
      }
      return re.test(String(value)) ? null : fail();
    }
    case 'minLength': {
      const limit = toNumber(rule.value);
      if (limit === null) return null;
      return String(value).length >= limit ? null : fail();
    }
    case 'maxLength': {
      const limit = toNumber(rule.value);
      if (limit === null) return null;
      return String(value).length <= limit ? null : fail();
    }
    case 'min': {
      const limit = toNumber(rule.value);
      const actual = toNumber(value);
      // Ei-numeerinen arvo numeerisessa säännössä: ei vertailukelpoinen, ei
      // virhe. Tyyppivirheen paikka on kenttätyypissä, ei tässä.
      if (limit === null || actual === null) return null;
      return actual >= limit ? null : fail();
    }
    case 'max': {
      const limit = toNumber(rule.value);
      const actual = toNumber(value);
      if (limit === null || actual === null) return null;
      return actual <= limit ? null : fail();
    }
    case 'email':
      return EMAIL_PATTERN.test(String(value)) ? null : fail();
    default:
      return null;
  }
}

/**
 * Ajaa säännöt arvoja vasten. `existing` on per-kenttä-`required`ista jo
 * syntyneet virheet. Ensimmäinen osuma per kenttä voittaa, sääntöjen
 * järjestyksessä.
 *
 * <p><b>Poikkeus (WP-AR8, `D7` = B):</b> jos kentällä on jo virhe
 * per-kenttä-`required`ista ja sääntö, joka epäonnistuu samalla arvolla, kantaa
 * OMAA `message`ään, säännön viesti korvaa sisäänrakennetun tekstin. Ennen tätä
 * sääntöviesti ei ajautunut koskaan: rakentaja kirjoitti viestin, näki sen
 * katoavan eikä saanut mitään vihjettä siitä miksi. `SKILL.md` §6c lupasi
 * nimenomaan päinvastaista kuin §7.1 (löydös F-G, todennettu selaimessa).
 * Validiteetti ei muutu — vain teksti.
 *
 * <p>Rivikentän sääntö (`rows[].hours`, {@link parseRowField}) ajetaan jokaisella
 * rivillä `values[rows]`-taulukosta, ja kaikki yllä oleva pätee rivin
 * indeksoituun avaimeen (`rows[1].hours`). Jos repeateria ei ole, sääntö
 * käsitellään litteänä nimenä kuten ennenkin.
 *
 * @returns UUSI virhekartta (existing + sääntöosumat) — syötettä ei mutatoida.
 */
export function evaluateRules(
  rules: readonly ValidationRule[],
  values: Readonly<Record<string, unknown>>,
  existing: Readonly<Record<string, string>> = {},
  /**
   * Kentät jotka lomakkeessa OVAT mutta jotka on piilotettu (WP-SF3, löydös
   * T-B). Niiden säännöt ohitetaan: piilotettu kenttä ei ole kysytty kenttä.
   *
   * Tämä on eri asia kuin kenttä jota lomakkeessa ei ole lainkaan — sellaisen
   * `required` on edelleen virhe, koska se on kirjoitusvirhe säännössä eikä
   * ehdollinen kenttä (ks. testi "ignores rules for fields the form does not
   * have"). Ero on juuri se että piilotetun kentän olemassaolon tietää vain
   * kutsuja, joka näkee DOMin. Rivin piilotettu kenttä annetaan indeksoidulla
   * avaimellaan (`rows[0].hours`).
   */
  hiddenFields: ReadonlySet<string> = new Set(),
): Record<string, string> {
  const errors: Record<string, string> = { ...existing };
  // Kentät joiden teksti tulee vielä sisäänrakennetusta requiredista ja jotka
  // säännön oma viesti saa siis kertaalleen korvata.
  const builtIn = new Set(Object.keys(existing));
  const check = (rule: ValidationRule, key: string, value: unknown) => {
    if (hiddenFields.has(key)) return;
    const hasError = errors[key] !== undefined;
    if (hasError && !(builtIn.has(key) && rule.message)) return;
    const message = applyRule(rule, value);
    if (!message) return;
    errors[key] = message;
    builtIn.delete(key);
  };
  for (const rule of rules) {
    const row = parseRowField(rule.field);
    const rows = row ? values[row.list] : undefined;
    if (row && Array.isArray(rows)) {
      rows.forEach((rowValues, index) => {
        const value = rowValues && typeof rowValues === 'object'
          ? (rowValues as Record<string, unknown>)[row.field]
          : undefined;
        check(rule, `${row.list}[${index}].${row.field}`, value);
      });
      continue;
    }
    check(rule, rule.field, values[rule.field]);
  }
  return errors;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}
